import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';
import { useLocality } from '@/hooks/usePostalAreas';
import {
  getCountyBySlug,
  getMunicipalityBySlug,
  getLocalityBySlug,
} from '@/lib/postal-snapshot';
import { PostalBreadcrumb } from '@/components/postal/PostalBreadcrumb';
import { PostalStatsCards } from '@/components/postal/PostalStatsCards';
import { PopulationChart } from '@/components/postal/PopulationChart';
import { IncomeChart } from '@/components/postal/IncomeChart';
import { PropertyChart } from '@/components/postal/PropertyChart';
import { SEO, getPlaceSchema, getBreadcrumbSchema } from '@/hooks/useSEO';

export default function PostalLocalityPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { countySlug, municipalitySlug, localitySlug } = useParams<{
    countySlug: string;
    municipalitySlug: string;
    localitySlug: string;
  }>();

  // Sync lookups for SSG-rendered SEO/headline content.
  const countySnap = getCountyBySlug(countySlug);
  const muniSnap = getMunicipalityBySlug(countySnap?.code, municipalitySlug);
  const localitySnap = getLocalityBySlug(muniSnap?.code, localitySlug);

  const { data: localityFresh } = useLocality(localitySnap?.code ?? '');

  const county = countySnap;
  const municipality = muniSnap;
  const locality = localityFresh ?? localitySnap;

  if (!locality) {
    return (
      <div className="container py-12">
        <p>{t('postal.notFound')}</p>
      </div>
    );
  }

  const countyPath = `/information/postnummer/${countySlug}`;
  const muniPath = `${countyPath}/${municipalitySlug}`;
  const localityPath = `${muniPath}/${localitySlug}`;
  const localityUrl = `https://postnummerservice.se/${lang}${localityPath}`;
  const seoDescription = `${locality.name} i ${municipality?.name ?? ''} kommun har ${locality.population_total.toLocaleString('sv-SE')} invånare och ${locality.postcode_count} postnummer. Statistik om befolkning, inkomst och fastigheter.`;

  return (
    <div className="container py-6 md:py-12 lg:py-16">
      <SEO
        title={`${locality.name} – ${t('navigation.postalCodes')}`}
        description={seoDescription}
        path={localityPath}
        jsonLd={[
          getBreadcrumbSchema([
            { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
            { name: t('navigation.postalCodes'), url: `https://postnummerservice.se/${lang}/information/postnummer` },
            { name: county?.name ?? countySlug!, url: `https://postnummerservice.se/${lang}${countyPath}` },
            { name: municipality?.name ?? municipalitySlug!, url: `https://postnummerservice.se/${lang}${muniPath}` },
            { name: locality.name, url: localityUrl },
          ]),
          getPlaceSchema({
            name: locality.name,
            description: seoDescription,
            url: localityUrl,
            containedIn: municipality?.name,
            population: locality.population_total,
            area: locality.area ?? undefined,
          }),
        ]}
      />
      <div className="max-w-5xl mx-auto space-y-5 md:space-y-8">
        <PostalBreadcrumb
          levels={[
            { label: county?.name ?? countySlug!, href: `/${lang}${countyPath}` },
            { label: municipality?.name ?? municipalitySlug!, href: `/${lang}${muniPath}` },
            { label: locality.name },
          ]}
        />

        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{locality.name}</h1>
          <p className="text-muted-foreground">{t('postal.localityDescription', { name: locality.name, municipality: municipality?.name ?? '' })}</p>
          <p className="text-sm text-muted-foreground mt-2">{t('postal.localityIntro', { name: locality.name, municipality: municipality?.name ?? '', population: locality.population_total.toLocaleString('sv-SE'), postalCodes: locality.postcode_count })}</p>
        </div>

        <PostalStatsCards
          population={locality.population_total}
          postalCodes={locality.postcode_count}
          area={locality.area ?? undefined}
          medianIncome={localityFresh?.income_data?.income_median}
        />

        {localityFresh && (
          <>
            <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
              <PopulationChart data={localityFresh.population_data} />
              <IncomeChart data={localityFresh.income_data} />
            </div>

            <PropertyChart data={localityFresh.properties_data} />
          </>
        )}
      </div>
    </div>
  );
}
